import { createTransform } from 'redux-persist';
import type { Middleware } from '@reduxjs/toolkit';

import type { OrchestrateState } from 'store/orchestrate-signup/orchestrate.types';
import type { SignUpProps } from 'store/signup/signup.types';
import type { UserState } from 'store/user/user.types';

import {
  clearAllSessionAuthTokens,
  getSessionAuthTokens,
  getSessionOrchestrateAuthTokens,
  getSessionSignUpAuthTokens,
  setSessionAuthTokens,
  setSessionOrchestrateAuthTokens,
  setSessionSignUpAuthTokens
} from 'utils/storage';

const USER_CLEAR_TYPE = 'user/clearUser';
const USER_SET_TOKEN_TYPE = 'user/setToken';
const USER_SET_USER_TYPE = 'user/setUser';
const SIGN_UP_CLEAR_USER_TYPE = 'signUp/clearUserState';
const SIGN_UP_SET_USER_TYPE = 'signUp/setUserState';
const ORCHESTRATE_CLEAR_TYPE = 'orchestrate/clearOrchestrateState';
const ORCHESTRATE_SET_USER_TYPE = 'orchestrate/setUserInfo';
const ORCHESTRATE_SET_WHOLE_STATE_TYPE = 'orchestrate/setWholeState';

type AuthSessionState = {
  orchestrate?: Partial<OrchestrateState>;
  signUp?: {
    user?: Partial<SignUpProps['user']>;
  };
  user?: Partial<UserState>;
};

// The login service restores its own session on load, so its key is never persisted either.
export const sanitizeUserPersist = (state: UserState): UserState => ({
  ...state,
  accessToken: '',
  authSessionKey: '',
  authSource: '',
  refreshToken: ''
});

export const sanitizeSignUpPersist = (state: SignUpProps): SignUpProps => ({
  ...state,
  user: {
    ...state.user,
    accessToken: '',
    refreshToken: ''
  }
});

export const sanitizeOrchestratePersist = (state: OrchestrateState): OrchestrateState => {
  const {
    accessToken: _accessToken,
    partnerToken: _partnerToken,
    restoreToken: _restoreToken,
    ...rest
  } = state;

  return {
    ...rest,
    user: {
      ...state.user,
      accessToken: undefined
    }
  };
};

const restoreUserPersistFromSession = (state: UserState): UserState => ({
  ...sanitizeUserPersist(state),
  ...getSessionAuthTokens()
});

const restoreSignUpPersistFromSession = (state: SignUpProps): SignUpProps => ({
  ...sanitizeSignUpPersist(state),
  user: {
    ...sanitizeSignUpPersist(state).user,
    ...getSessionSignUpAuthTokens()
  }
});

const restoreOrchestratePersistFromSession = (state: OrchestrateState): OrchestrateState => {
  const sanitizedState = sanitizeOrchestratePersist(state);
  const sessionTokens = getSessionOrchestrateAuthTokens();

  return {
    ...sanitizedState,
    ...sessionTokens,
    user: {
      ...sanitizedState.user,
      accessToken: sessionTokens.accessToken
    }
  };
};

export const sanitizeAuthPersistForStorage = (
  inboundState: unknown,
  key: number | string
): unknown => {
  switch (key) {
    case 'user':
      return sanitizeUserPersist(inboundState as UserState);
    case 'signUp':
      return sanitizeSignUpPersist(inboundState as SignUpProps);
    case 'orchestrate':
      return sanitizeOrchestratePersist(inboundState as OrchestrateState);
    default:
      return inboundState;
  }
};

export const restoreAuthPersistFromStorage = (
  outboundState: unknown,
  key: number | string
): unknown => {
  switch (key) {
    case 'user':
      return restoreUserPersistFromSession(outboundState as UserState);
    case 'signUp':
      return restoreSignUpPersistFromSession(outboundState as SignUpProps);
    case 'orchestrate':
      return restoreOrchestratePersistFromSession(outboundState as OrchestrateState);
    default:
      return outboundState;
  }
};

export const authPersistTransform = createTransform(
  sanitizeAuthPersistForStorage,
  restoreAuthPersistFromStorage,
  {
    whitelist: ['user', 'signUp', 'orchestrate']
  }
);

const syncUserTokens = (state: AuthSessionState) => {
  if (state.user?.authSessionKey) {
    return;
  }

  setSessionAuthTokens({
    accessToken: state.user?.accessToken,
    refreshToken: state.user?.refreshToken
  });
};

const syncSignUpTokens = (state: AuthSessionState) => {
  setSessionSignUpAuthTokens({
    accessToken: state.signUp?.user?.accessToken,
    refreshToken: state.signUp?.user?.refreshToken
  });
};

const syncOrchestrateTokens = (state: AuthSessionState) => {
  const orchestrateAccessToken =
    state.orchestrate?.accessToken ?? state.orchestrate?.user?.accessToken;

  setSessionOrchestrateAuthTokens({
    accessToken: orchestrateAccessToken,
    partnerToken: state.orchestrate?.partnerToken,
    restoreToken: state.orchestrate?.restoreToken
  });
};

export const syncAuthTokensToSessionMiddleware: Middleware = (api) => (next) => (action) => {
  const result = next(action);
  const actionType = (action as { type?: string }).type;
  const state = api.getState() as AuthSessionState;

  switch (actionType) {
    case USER_CLEAR_TYPE:
      clearAllSessionAuthTokens();
      break;
    case USER_SET_TOKEN_TYPE:
    case USER_SET_USER_TYPE:
      syncUserTokens(state);
      break;
    case SIGN_UP_CLEAR_USER_TYPE:
      syncSignUpTokens(state);
      break;
    case SIGN_UP_SET_USER_TYPE:
      syncSignUpTokens(state);
      break;
    case ORCHESTRATE_CLEAR_TYPE:
      syncOrchestrateTokens(state);
      break;
    case ORCHESTRATE_SET_USER_TYPE:
    case ORCHESTRATE_SET_WHOLE_STATE_TYPE:
      syncOrchestrateTokens(state);
      break;
    default:
      break;
  }

  return result;
};
